/**
 * Helpers for PMA `tpTrends2WatchList` (3 Trends to Watch) rows from getPMADetails.
 * Row shape includes `t2W*` (current / editable) and `orgT2W*` (original) fields.
 */

/** Escape text for safe insertion into a single HTML paragraph. */
export function escapeHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function plainTextToStatementHtml(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return "";
  if (/[<>]/.test(raw)) return raw;
  return `<p>${escapeHtml(raw)}</p>`;
}

/** API value when the "A Seller's Advantage" footer checkbox is on. */
export const T2W_DEFAULT_FOOTER_TEXT = "A Sellers Advantage";
export const T2W_FOOTER_DISPLAY_LABEL = "— A Seller's Advantage";

export function isTrendsFooterOn(value) {
  return value != null && String(value).trim() !== "";
}

export function footerToggleValue(checked) {
  return checked ? T2W_DEFAULT_FOOTER_TEXT : null;
}

/** Use the current t2W footer as-is; null/empty means the footer is off. */
export function pickFooterField(row, t2wKey, orgKey) {
  if (!row) return "";
  if (Object.prototype.hasOwnProperty.call(row, t2wKey)) {
    return isTrendsFooterOn(row[t2wKey]) ? row[t2wKey] : "";
  }
  return isTrendsFooterOn(row[orgKey]) ? row[orgKey] : "";
}

/** Return footer as-is so any API/footer text can be displayed. */
export function footerLabelToBenefit(footer) {
  return String(footer ?? "").trim();
}

/**
 * Keys on each `tpTrends2WatchList` object from getPMADetails.
 * Area identity is `t2WAreaID` (same value as the agent's `mlsAreaID`).
 */
export const T2W_API_ROW_KEYS = [
  "isTrendsEditedByAgent",
  "dateCreatedOrEdited",
  "t2WAreaID",
  "t2WTitle",
  "t2WSubTitle",
  "t2WAreaName",
  "t2WHeading1",
  "t2WBullet1",
  "t2WText1",
  "t2WFooter1",
  "t2WHeading2",
  "t2WBullet2",
  "t2WText2",
  "t2WFooter2",
  "t2WHeading3",
  "t2WBullet3",
  "t2WText3",
  "t2WFooter3",
  "t2WSummaryHeading",
  "t2WSummaryText",
  "orgT2WTitle",
  "orgT2WSubTitle",
  "orgT2WAreaName",
  "orgT2WHeading1",
  "orgT2WBullet1",
  "orgT2WText1",
  "orgT2WFooter1",
  "orgT2WHeading2",
  "orgT2WBullet2",
  "orgT2WText2",
  "orgT2WFooter2",
  "orgT2WHeading3",
  "orgT2WBullet3",
  "orgT2WText3",
  "orgT2WFooter3",
  "orgT2WSummaryHeading",
  "orgT2WSummaryText",
];

function sameAreaId(a, b) {
  return String(a ?? "") === String(b ?? "");
}

function toApiFieldValue(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return value;
}

/** Keep only known DTO keys that already exist on the row. Do not fill missing keys with null. */
export function toApiTrendsRow(row) {
  const next = {};
  T2W_API_ROW_KEYS.forEach((key) => {
    if (row && Object.prototype.hasOwnProperty.call(row, key)) {
      next[key] = row[key];
    }
  });
  return next;
}

export function toApiTrendsList(tpTrends2WatchList) {
  return (Array.isArray(tpTrends2WatchList) ? tpTrends2WatchList : []).map(
    (row) => toApiTrendsRow(row),
  );
}

function copyOriginalOrgFields(fromRow, toRow) {
  T2W_API_ROW_KEYS.forEach((key) => {
    if (!key.startsWith("org")) return;
    toRow[key] =
      fromRow && Object.prototype.hasOwnProperty.call(fromRow, key)
        ? fromRow[key]
        : null;
  });
  return toRow;
}

/**
 * Pick the list row whose `t2WAreaID` matches `currentArea.mlsAreaID`.
 */
export function pickTrends2WatchRowForArea(tpTrends2WatchList, mlsAreaID) {
  if (!Array.isArray(tpTrends2WatchList) || tpTrends2WatchList.length === 0) {
    return null;
  }
  if (!mlsAreaID) return null;

  const row = tpTrends2WatchList.find((r) =>
    sameAreaId(getTrendsRowAreaId(r), mlsAreaID),
  );

  if (row) return row;

  if (String(mlsAreaID).includes("DEMO")) {
    return tpTrends2WatchList[0] || null;
  }

  return null;
}

/** Prefer edited `t2W*` value, then `orgT2W*`, then fallback. */
export function pickT2wField(row, t2wKey, orgKey, fallback = "") {
  if (!row) return fallback;
  const cur = row[t2wKey];
  if (cur !== undefined && cur !== null && String(cur).trim() !== "") {
    return cur;
  }
  const org = row[orgKey];
  if (org !== undefined && org !== null && String(org).trim() !== "") {
    return org;
  }
  return fallback;
}

/**
 * Build three "sentence" objects compatible with SentenceTrendsIndicator
 * from a tpTrends2Watch row (uses pickT2wField for t2W vs org).
 */
export function rowToOverrideSentences(row) {
  if (!row) return null;

  const slots = [
    {
      tH: "t2WHeading1",
      oH: "orgT2WHeading1",
      tT: "t2WText1",
      oT: "orgT2WText1",
      tF: "t2WFooter1",
      oF: "orgT2WFooter1",
    },
    {
      tH: "t2WHeading2",
      oH: "orgT2WHeading2",
      tT: "t2WText2",
      oT: "orgT2WText2",
      tF: "t2WFooter2",
      oF: "orgT2WFooter2",
    },
    {
      tH: "t2WHeading3",
      oH: "orgT2WHeading3",
      tT: "t2WText3",
      oT: "orgT2WText3",
      tF: "t2WFooter3",
      oF: "orgT2WFooter3",
    },
  ];

  return slots.map((keys) => {
    const header = pickT2wField(row, keys.tH, keys.oH, "");
    const bodyText = pickT2wField(row, keys.tT, keys.oT, "");
    const footer = pickFooterField(row, keys.tF, keys.oF);
    return {
      header: String(header),
      statement: plainTextToStatementHtml(bodyText),
      logic: "",
      benefit: footerLabelToBenefit(footer),
      categoryId: null,
    };
  });
}

export function rowToHeaderLines(row) {
  if (!row) return { main: null, sub: null };
  return {
    main: pickT2wField(row, "t2WTitle", "orgT2WTitle", null),
    sub: pickT2wField(row, "t2WSubTitle", "orgT2WSubTitle", null),
  };
}

export const T2W_FIELD_GROUPS = {
  header: {
    title: "Edit Title & Area",
    fields: [
      {
        key: "t2WTitle",
        orgKey: "orgT2WTitle",
        label: "Title",
        kind: "input",
        maxLength: 25,
      },
      {
        key: "t2WSubTitle",
        orgKey: "orgT2WSubTitle",
        label: "Subtitle",
        kind: "input",
        maxLength: 25,
      },
      {
        key: "t2WAreaName",
        orgKey: "orgT2WAreaName",
        label: "Area name",
        kind: "input",
      },
    ],
  },
  trend1: {
    title: "Edit Text in Section 1",
    headerVariant: "section",
    fields: [
      {
        key: "t2WHeading1",
        orgKey: "orgT2WHeading1",
        label: "Heading",
        kind: "input",
        maxLength: 30,
      },
      {
        key: "t2WText1",
        orgKey: "orgT2WText1",
        label: "Text",
        kind: "textarea",
        maxLength: 280,
        lined: true,
      },
      {
        key: "t2WFooter1",
        orgKey: "orgT2WFooter1",
        label: T2W_FOOTER_DISPLAY_LABEL,
        kind: "footerToggle",
      },
    ],
  },
  trend2: {
    title: "Edit Text in Section 2",
    headerVariant: "section",
    fields: [
      {
        key: "t2WHeading2",
        orgKey: "orgT2WHeading2",
        label: "Heading",
        kind: "input",
        maxLength: 30,
      },
      {
        key: "t2WText2",
        orgKey: "orgT2WText2",
        label: "Text",
        kind: "textarea",
        maxLength: 280,
        lined: true,
      },
      {
        key: "t2WFooter2",
        orgKey: "orgT2WFooter2",
        label: T2W_FOOTER_DISPLAY_LABEL,
        kind: "footerToggle",
      },
    ],
  },
  trend3: {
    title: "Edit Text in Section 3",
    headerVariant: "section",
    fields: [
      {
        key: "t2WHeading3",
        orgKey: "orgT2WHeading3",
        label: "Heading",
        kind: "input",
        maxLength: 30,
      },
      {
        key: "t2WText3",
        orgKey: "orgT2WText3",
        label: "Text",
        kind: "textarea",
        maxLength: 280,
        lined: true,
      },
      {
        key: "t2WFooter3",
        orgKey: "orgT2WFooter3",
        label: T2W_FOOTER_DISPLAY_LABEL,
        kind: "footerToggle",
      },
    ],
  },
  takeaway: {
    title: "Edit Take Away",
    headerVariant: "section",
    fields: [
      {
        key: "t2WSummaryHeading",
        orgKey: "orgT2WSummaryHeading",
        label: "Take Away heading",
        kind: "input",
        maxLength: 15,
      },
      {
        key: "t2WSummaryText",
        orgKey: "orgT2WSummaryText",
        label: "Take Away text",
        kind: "textarea",
        maxLength: 200,
      },
    ],
  },
};

export const T2W_EDITABLE_FIELDS = Object.values(T2W_FIELD_GROUPS).flatMap(
  (group) => group.fields,
);

export function getTrendsRowAreaId(row) {
  if (!row) return "";
  return row.t2WAreaID != null && String(row.t2WAreaID) !== ""
    ? String(row.t2WAreaID)
    : "";
}

export function formatTrendsEditedDate(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )}`;
}

/** Copy displayed (t2W or org) values into t2W* so the editor form is populated. */
export function hydrateT2wEditableFields(row) {
  if (!row) return null;
  const next = { ...row };
  T2W_EDITABLE_FIELDS.forEach(({ key, orgKey, kind }) => {
    if (kind === "footerToggle") {
      next[key] = Object.prototype.hasOwnProperty.call(row, key)
        ? row[key]
        : (row[orgKey] ?? null);
    } else {
      next[key] = pickT2wField(row, key, orgKey, "");
    }
  });
  return next;
}

export function buildSavedTrendsRow(row, edits, identity = {}) {
  if (!row) return null;
  const next = { ...row };
  Object.keys(edits || {}).forEach((key) => {
    if (!key.startsWith("t2W") || key.startsWith("org")) return;
    next[key] = toApiFieldValue(edits[key]);
  });
  copyOriginalOrgFields(row, next);
  delete next.mlsAreaID;
  delete next.ddAreaID;
  delete next.areaID;
  delete next.listgenUserID;
  next.t2WAreaID = row.t2WAreaID;
  next.isTrendsEditedByAgent = true;
  next.dateCreatedOrEdited = formatTrendsEditedDate();
  return next;
}

export function mergeTrendsRowIntoList(tpTrends2WatchList, savedRow) {
  const areaId = getTrendsRowAreaId(savedRow);
  const list = Array.isArray(tpTrends2WatchList)
    ? tpTrends2WatchList.map((row) => ({ ...row }))
    : [];
  if (!areaId) {
    return savedRow ? [...list, savedRow] : list;
  }
  let matched = false;
  const next = list.map((row) => {
    if (sameAreaId(getTrendsRowAreaId(row), areaId)) {
      matched = true;
      return savedRow;
    }
    return row;
  });
  return matched ? next : [...next, savedRow];
}

export function isTrendsFormDirty(
  baseline,
  current,
  fields = T2W_EDITABLE_FIELDS,
) {
  if (!baseline || !current) return false;
  return fields.some(({ key }) => {
    const a = String(baseline[key] ?? "");
    const b = String(current[key] ?? "");
    return a !== b;
  });
}

export function revertTrendsFormToOriginal(row, fields = T2W_EDITABLE_FIELDS) {
  if (!row) return null;
  const next = { ...row };
  fields.forEach(({ key, orgKey, kind }) => {
    if (kind === "footerToggle") {
      next[key] = isTrendsFooterOn(row[orgKey]) ? row[orgKey] : null;
    } else {
      next[key] = row[orgKey] != null ? String(row[orgKey]) : "";
    }
  });
  return next;
}

/** True when every listed t2W field already matches its orgT2W counterpart. */
export function isTrendsFormAtOriginal(formData, fields = T2W_EDITABLE_FIELDS) {
  if (!formData || !Array.isArray(fields) || fields.length === 0) return true;
  return fields.every(({ key, orgKey }) => {
    return String(formData[key] ?? "") === String(formData[orgKey] ?? "");
  });
}

export function isTrendsSectionEdited(row, fields) {
  if (!row || !Array.isArray(fields)) return false;
  return fields.some(({ key, orgKey }) => {
    const current = String(row[key] ?? "").trim();
    const original = String(row[orgKey] ?? "").trim();
    return current !== original;
  });
}

export function isTrendsFieldOverLimit(value, maxLength) {
  if (maxLength == null) return false;
  return String(value ?? "").length > maxLength;
}

export function isTrendsFormOverLimit(formData, fields = T2W_EDITABLE_FIELDS) {
  if (!formData || !Array.isArray(fields)) return false;
  return fields.some(({ key, maxLength }) =>
    isTrendsFieldOverLimit(formData[key], maxLength),
  );
}
